import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  getSingleDevice,
  doGetBundle,
  updateDevicesPageActiveTab,
  updatePivotOverviewPageSelectedDevices,
  searchDevicesV2,
  updatePivotOverviewPageSingleSelectedDevice,
  doGetBundleCommissionInstances,
  updateBundlesDictionary,
  setPage
} from 'redux/actions';
import { getServiceUnits } from 'common/utils/helpers';
import DeviceTypePageTemplate from '../../components/shared/DeviceTypePageTemplate/DeviceTypePageTemplate';
import { DEVICES_PAGE_TAB, LOADING_CIRCLE, Status } from 'common/utils/constants';
import { ReturnButton } from 'components/shared/ReturnButton/ReturnButton';
import CommissionErrorBox from 'components/features/Commissioning/CommissionErrorBox/CommissionErrorBox';
import './PivotOverviewPage.css';
import {
  COMMISSIONING_SERVICE_RELEASE_DATE,
  GET_LAST_COMMISSION_DATE_STATUS,
  getLastSuccessfulCommissionDate
} from './utils';
import { mapDeviceSearchResult } from 'apis/SearchDevices';
import { isGa2Device, getGa2DeviceMembers } from 'common/utils/deviceTwinUtils';

const PivotOverviewPage = () => {
  const { search } = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(search), [search]);
  const donorId = searchParams.get('deviceId');
  const selectedDevices = useSelector((store) => store.ui.pivotOverviewPage.selectedDevices); // DU and SU(s)
  const donorDevice = selectedDevices?.[donorId]; // DU
  const isGa2 = isGa2Device(donorDevice);
  const [bundle, setBundle] = useState(null);
  const [serviceDeviceIds, setServiceDeviceIds] = useState([]); // SU(s)
  const [lastCommissionDate, setLastCommissionDate] = useState(0);
  const [getBundleError, setGetBundleError] = useState(null);
  const [getDeviceError, setGetDeviceError] = useState(null);
  const [isInitialGetBundleCalled, setIsInitialGetBundleCalled] = useState(false);
  const [isInitialGetDeviceCalled, setIsInitialGetDeviceCalled] = useState(false);
  const serviceDevices = useMemo(() => {
    if (isGa2) {
      return getGa2DeviceMembers(selectedDevices[donorId]).filter((device) => device.productType === 'SERVICE');
    }

    return serviceDeviceIds.map((serviceId) => selectedDevices[serviceId]).filter(Boolean);
  }, [isGa2, donorId, serviceDeviceIds, selectedDevices]);

  const getBundleStatus = useSelector((store) => store.statuses.bundles?.[donorDevice?.tags?.bundle?.bundleId]);
  const getDeviceStatus = useSelector((store) => store.statuses[`GET-${donorId}`]);
  const storedBundle = useSelector(store => store.data.bundlesStorage.storedBundlesDictionary[donorDevice?.tags?.bundle?.bundleId])

  const mounted = useRef();
  const dispatch = useDispatch();

  const updateDevices = useCallback(
    (deviceIds) => {
      const cb_success = (result) => {
        const devices = result.devices;
        const serviceUnits = getServiceUnits(devices);
        setServiceDeviceIds(serviceUnits.map((device) => device.deviceId));

        const updatedDevices = devices.reduce((acc, cur) => {
          acc[cur.deviceId] = cur;
          return acc;
        }, {});
        dispatch(updatePivotOverviewPageSelectedDevices(updatedDevices));
      };

      const cb_failed = (error) => {
        setGetDeviceError(error.message);
      };

      const searchDevicesV2Body = {
        size: deviceIds.length,
        coalesceBundles: false,
        filters: {
          deviceId: deviceIds
        },
        returnFields: ['deviceId', 'tags', 'properties', 'status', 'connectionState', 'lastActivityTime']
      };

      dispatch(searchDevicesV2(searchDevicesV2Body, null, cb_success, cb_failed));
    },
    [dispatch]
  );

  const updateBundle = useCallback(
    (bundleId) => {
      dispatch(
        doGetBundle(
          bundleId,
          (result) => {
            const members = result.members;
            const deviceIds = members.map((member) => member.deviceId);
            const serviceUnits = getServiceUnits(members);
            result['bundleId'] = bundleId;

            const data = mapDeviceSearchResult({ bundles: [result] });

            dispatch(updateBundlesDictionary(data.bundles));
            setServiceDeviceIds(serviceUnits.map((device) => device.deviceId));
            updateDevices(deviceIds);
            setBundle(result);
          },
          (error) => {
            setGetBundleError(error.message);
          }
        )
      );
    },
    [dispatch]
  );

  const updateSingleDevice = useCallback(
    (deviceId) => {
      dispatch(
        getSingleDevice(deviceId, (res) => {
          if (res?.message) {
            setGetDeviceError(res.message);
          } else {
            dispatch(updatePivotOverviewPageSingleSelectedDevice(res));
          }
        })
      ); //do a single get device when single device page is loaded
    },
    [dispatch]
  );

  const updateLastCommissionDate = useCallback(
    (bundleId) => {
      const cb_success = (res) => {
        setLastCommissionDate(getLastSuccessfulCommissionDate(res));
      };
      const cb_fail = (error) => {
        setLastCommissionDate(GET_LAST_COMMISSION_DATE_STATUS.NOT_FOUND);
      };
      dispatch(
        doGetBundleCommissionInstances(
          'inactive',
          COMMISSIONING_SERVICE_RELEASE_DATE,
          'now',
          bundleId,
          cb_success,
          cb_fail
        )
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!mounted.current) {
      // do componentDidMount logic
      dispatch(setPage('PivotOverviewPage'));
      updateSingleDevice(donorId, (result) => {
        dispatch(updatePivotOverviewPageSingleSelectedDevice(result));
      });
      setIsInitialGetDeviceCalled(true);
      mounted.current = true;
    }
  }, []);

  useEffect(() => {
    if (donorDevice?.id != null) {
      if (donorDevice?.tags?.bundle?.bundleId && !isInitialGetBundleCalled) {
        setIsInitialGetBundleCalled(true);
        const bundleId = donorDevice.tags.bundle.bundleId;
        updateBundle(bundleId);
        updateLastCommissionDate(bundleId);
      }
    }
  }, [donorDevice, isInitialGetBundleCalled]);

  useEffect(() => {
    setBundle(storedBundle)
  }, [storedBundle]);

  let overlay = null;
  if (getDeviceError) {
    overlay = (
      <CommissionErrorBox
        title={`Get Device Error: ${getDeviceError}`}
        message={'Something went wrong. Please try again later.'}
        onClose={() => {
          setGetDeviceError(null);
          overlay = null;
        }}
      />
    );
  } else if (getBundleError) {
    overlay = (
      <CommissionErrorBox
        title={`Get Bundle Error: ${getBundleError}`}
        message={'Something went wrong. Please try again later.'}
        onClose={() => {
          setGetBundleError(null);
        }}
      />
    );
  } else if (
    !isInitialGetDeviceCalled &&
    !isInitialGetBundleCalled &&
    (getBundleStatus === Status.PENDING ||
      getDeviceStatus === Status.PENDING ||
      (!isGa2 && bundle?.members?.length !== serviceDevices?.length + 1))
  ) {
    overlay = (
      <div id="Global_LoadingCircle_Overlay" className="Global_LoadingCircle_Overlay">
        {LOADING_CIRCLE}
      </div>
    );
  }

  return (
    <div id="PivotOverviewPageContainer" className="PivotOverviewPageContainer">
      {overlay}
      <ReturnButton
        text={`Return to ${isGa2 ? 'All Devices' : 'Pivot 5G'} page`}
        route={'/console/devices'}
        action={() => {
          dispatch(updatePivotOverviewPageSelectedDevices({}));
          dispatch(updateDevicesPageActiveTab(isGa2 ? DEVICES_PAGE_TAB.all : DEVICES_PAGE_TAB.pivot));
        }}
      />
      <DeviceTypePageTemplate
        device={donorDevice}
        deviceType={'pivot'}
        isGa2={isGa2}
        serviceDevices={serviceDevices}
        bundleMembers={Object.values(selectedDevices)}
        bundle={bundle}
        lastCommissionDate={lastCommissionDate}
        updateBundle={updateBundle}
      />
    </div>
  );
};

export default PivotOverviewPage;
